import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Fragment, Suspense, lazy, useEffect } from "react";
import { lineSpinner } from "ldrs";

import { ThemeProvider } from "styled-components";
import GlobalStyles from "./styles/globalStyles";
import theme from "./styles/theme";
import "./styles/index.css";

import ProtectedRoute from "./components/utils/PrivateRoute";
import MetaPixelPageView from "./components/utils/MetaPixelPageView";
import { RadioProvider } from "./components/Radio/RadioContext";
import RadioWidget from "./components/Radio/RadioWidget";

import { selectLoggedIn, setLoggedIn } from "./store/slices/auth/slice";
import { refresh } from "./store/slices/auth/asyncThunks";
import { getAdminUserInfo } from "./store/slices/user/asyncThunks";
import { getCategories } from "./store/slices/products/asyncThunks";
import { getBandsForDropdown } from "./store/slices/bands/asyncThunks";
import { getFestivalsForDropdown } from "./store/slices/festivals/asyncThunks";

import Homepage from "./screens/Portal/Homepage/HomePage";
import LoginPage from "./screens/Portal/LoginPage/LoginPage";
import ShopPage from "./screens/Portal/ShopPage/ShopPageNew";
import ProductPage from "./screens/Portal/ProductPage/ProductPage";
import NewsPage from "./screens/Portal/NewsPage/NewsPage";
import BandsPage from "./screens/Portal/BandsPage/BandsPage";
import AboutPage from "./screens/Portal/AboutPage/AboutPage";
import CartPage from "./screens/Portal/CartPage/CartPage";
import OrderSuccessPage from "./screens/Portal/OrderSuccessPage/OrderSuccessPage";
import BandDetailsPage from "./screens/Portal/BandDetailsPage/BandDetailsPage";
import FestivalDetailsPage from "./screens/Portal/FestivalDetailsPage/FestivalDetailsPage";
import NewsDetailsPage from "./screens/Portal/NewsDetailsPage/NewsDetailsPage";
import EmailVerificationPage from "./components/utils/EmailVerificationPage";
import NotFoundPage from "./screens/Portal/NotFoundPage/NotFoundPage";
import { Toaster } from "sonner";
import { toastBaseStyle } from "./components/modern-ui/sonner";
import LegalPage from "./screens/Portal/LegalPage/LegalPage";
import { PageSkeleton, PanelSkeleton } from "./components/utils/Skeleton";

/* Admin/band panel se lazy-loaduje, kupci ne skidaju kod panela.
   Webpack pravi zasebne chunkove po stranici (code splitting). */
const AdminDashboardPage = lazy(() => import("./screens/Admin/AdminDashboardPage/AdminDashboardPage"));
const AdminAnalyticsPage = lazy(() => import("./screens/Admin/AdminAnalyticsPage/AdminAnalyticsPage."));
const AdminReportsPage = lazy(() => import("./screens/Admin/AdminReportsPage/AdminReportsPage"));
const AdminProductsPage = lazy(() => import("./screens/Admin/AdminProductsPage/AdminProductsPage"));
const AdminProductFormPage = lazy(() => import("./screens/Admin/AdminProductFormPage/AdminProductFormPage"));
const ProductDimensionsPage = lazy(() => import("./screens/Admin/ProductDimensionsPage/ProductDimensionsPage"));
const AdminCategoryPage = lazy(() => import("./screens/Admin/AdminCategoryPage/AdminCategoryPage"));
const AdminCategoryFormPage = lazy(() => import("./screens/Admin/AdminCategoryFormPage/AdminCategoryFormPage"));
const AdminOrdersPage = lazy(() => import("./screens/Admin/AdminOrdersPage/AdminOrdersPage"));
const AdminOrderDetailsPage = lazy(() => import("./screens/Admin/AdminOrderDetailsPage/AdminOrderDetailsPage"));
const AdminBandsPage = lazy(() => import("./screens/Admin/AdminBandsPage/AdminBandsPage"));
const AdminBandFormPage = lazy(() => import("./screens/Admin/AdminBandFormPage/AdminBandFormPage"));
const AdminNewsPage = lazy(() => import("./screens/Admin/AdminNewsPage/AdminNewsPage"));
const AdminNewsDetailsPage = lazy(() => import("./screens/Admin/AdminNewsDetailsPage/AdminNewsDetailsPage"));
const AdminNewslettersPage = lazy(() => import("./screens/Admin/AdminNewslettersPage/AdminNewslettersPage"));
const AdminSupportPage = lazy(() => import("./screens/Admin/AdminSupportPage/AdminSupportPage"));
const AdminEmailLogsPage = lazy(() => import("./screens/Admin/AdminEmailLogsPage/AdminEmailLogsPage"));
const AdminEmailLogDetailsPage = lazy(() => import("./screens/Admin/AdminEmailLogDetailsPage/AdminEmailLogDetailsPage"));
const AdminRadioPage = lazy(() => import("./screens/Admin/AdminRadioPage/AdminRadioPage"));
const AdminRadioFormPage = lazy(() => import("./screens/Admin/AdminRadioPage/AdminRadioFormPage"));
const BandDashboardPage = lazy(() => import("./screens/Admin/BandDashboardPage/BandDashboardPage"));
const BandProductsPage = lazy(() => import("./screens/Admin/BandProductsPage/BandProductsPage"));
const BandProductFormPage = lazy(() => import("./screens/Admin/BandProductsPage/BandProductFormPage"));
const BandOrdersPage = lazy(() => import("./screens/Admin/BandOrdersPage.js/BandOrdersPage.js"));
const BandOrderDetailsPage = lazy(() => import("./screens/Admin/BandOrdersPage.js/BandOrderDetailsPage"));
const BandReportsPage = lazy(() => import("./screens/Admin/BandReportsPage/BandReportsPage"));
const BandSettings = lazy(() => import("./screens/Admin/BandSettings/BandSettings"));

/* Fallback dok se chunk panela skida */
const RouteFallback = () => {
  // Panel rute dobijaju panel skeleton (sidebar + tabela), javne portal oblik
  const path = window.location.pathname;
  const isPanel =
    path.startsWith("/admin-panel") || path.startsWith("/bend-panel");
  return isPanel ? <PanelSkeleton /> : <PageSkeleton full />;
};

lineSpinner.register();

/* Stare "Upravljaj detaljima" rute preusmerene na spojenu stranicu izmene */
const BandEditRedirect = () => {
  const { bandId } = useParams();
  return <Navigate to={`/admin-panel/bendovi/${bandId}/izmena`} replace />;
};

const FestivalEditRedirect = () => {
  const { festivalId } = useParams();
  return (
    <Navigate to={`/admin-panel/festivali/${festivalId}/izmena`} replace />
  );
};

function App() {
  const loggedIn = useSelector(selectLoggedIn);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCategories())
      .unwrap()
      .then(() => {})
      .catch((err) => console.error(err.message));
    dispatch(getBandsForDropdown())
      .unwrap()
      .then(() => {})
      .catch((err) => console.error(err.message));
    dispatch(getFestivalsForDropdown())
      .unwrap()
      .then(() => {})
      .catch((err) => console.error(err.message));
  }, [dispatch]);

  useEffect(() => {
    dispatch(refresh())
      .unwrap()
      .then(() => {
        dispatch(getAdminUserInfo())
          .unwrap()
          .then(() => {})
          .catch((err) => console.error(err.message));
      })
      .catch((error) => {
        dispatch(setLoggedIn({ loggedIn: false }));
      });
  }, [dispatch]);

  // Number inputi: skrol točkićem preko fokusiranog inputa menja vrednost,
  // skidamo fokus pa skrol pomera stranicu umesto da "šalta" brojeve.
  useEffect(() => {
    const onWheel = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number" && el === e.target) {
        el.blur();
      }
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <RadioProvider>
      <Fragment>
        <GlobalStyles />
        <MetaPixelPageView />

      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Javne rute */}
        <Route path="/" element={<Homepage />} />
        <Route path="/prodavnica" element={<ShopPage />} />
        <Route path="/proizvod/:id" element={<ProductPage />} />
        <Route path="/novosti" element={<NewsPage />} />
        <Route path="/novost/:newsId" element={<NewsDetailsPage />} />
        <Route path="/bendovi" element={<BandsPage />} />
        <Route path="/o-nama" element={<AboutPage />} />
        <Route path="/korpa" element={<CartPage />} />
        <Route path="/porudzbina-uspesna" element={<OrderSuccessPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Pravne stranice */}
        <Route path="/uslovi-kupovine" element={<LegalPage />} />
        <Route path="/politika-privatnosti" element={<LegalPage />} />
        <Route path="/reklamacije" element={<LegalPage />} />

        {/* Protected routes with role-based access — uvek mountovane;
            ProtectedRoute sam prikazuje skeleton dok se auth proverava,
            pa refresh na panel deep-linku ne pada u prazan catch-all */}
        <>
            {/* Admin routes - only accessible to admin role */}
            <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
              <Route
                path="/admin-panel/kontrolna-tabla"
                element={<AdminDashboardPage />}
              />
              <Route
                path="/admin-panel/analitika"
                element={<AdminAnalyticsPage />}
              />
              <Route
                path="/admin-panel/izvestaji"
                element={<AdminReportsPage />}
              />
              <Route
                path="/admin-panel/evidencija-slanja"
                element={<AdminEmailLogsPage />}
              />
              <Route
                path="/admin-panel/evidencija-slanja/:logId"
                element={<AdminEmailLogDetailsPage />}
              />
              <Route
                path="/admin-panel/proizvodi"
                element={<AdminProductsPage />}
              />
              <Route
                path="/admin-panel/proizvodi/novi"
                element={<AdminProductFormPage />}
              />
              <Route
                path="/admin-panel/proizvodi/:id/izmena"
                element={<AdminProductFormPage />}
              />
              <Route
                path="/admin-panel/proizvodi/:productId/dimenzije"
                element={<ProductDimensionsPage />}
              />
              {/* Legacy putanja, zadržana zbog starih linkova */}
              <Route
                path="/admin/product-dimensions/:productId"
                element={<ProductDimensionsPage />}
              />
              <Route
                path="/admin-panel/kategorije"
                element={<AdminCategoryPage />}
              />
              <Route
                path="/admin-panel/kategorije/nova"
                element={<AdminCategoryFormPage />}
              />
              <Route
                path="/admin-panel/kategorije/:id/izmena"
                element={<AdminCategoryFormPage />}
              />
              <Route
                path="/admin-panel/porudzbine"
                element={<AdminOrdersPage />}
              />
              <Route
                path="/admin-panel/porudzbine/:id"
                element={<AdminOrderDetailsPage />}
              />
              <Route path="/admin-panel/bendovi" element={<AdminBandsPage />} />
              <Route
                path="/admin-panel/bendovi/novi"
                element={<AdminBandFormPage entity="band" />}
              />
              <Route
                path="/admin-panel/bendovi/:id/izmena"
                element={<AdminBandFormPage entity="band" />}
              />
              <Route
                path="/admin-panel/festivali/novi"
                element={<AdminBandFormPage entity="festival" />}
              />
              <Route
                path="/admin-panel/festivali/:id/izmena"
                element={<AdminBandFormPage entity="festival" />}
              />
              <Route path="/admin-panel/novosti" element={<AdminNewsPage />} />
              <Route
                path="/admin-panel/newsletters"
                element={<AdminNewslettersPage />}
              />

              <Route
                path="/admin-panel/korisnicka-podrska"
                element={<AdminSupportPage />}
              />

              {/* Stare "Upravljaj detaljima" rute, spojeno u izmenu */}
              <Route
                path="/admin-panel/bend/:bandId"
                element={<BandEditRedirect />}
              />
              <Route
                path="/admin-panel/festival/:festivalId"
                element={<FestivalEditRedirect />}
              />
              <Route
                path="/admin-panel/novost/:newsId"
                element={<AdminNewsDetailsPage />}
              />
            </Route>

            {/* Band routes - only accessible to bend role */}
            <Route element={<ProtectedRoute allowedRoles={["band"]} />}>
              <Route
                path="/bend-panel/kontrolna-tabla"
                element={<BandDashboardPage />}
              />
              <Route
                path="/bend-panel/proizvodi"
                element={<BandProductsPage />}
              />
              <Route
                path="/bend-panel/proizvodi/novi"
                element={<BandProductFormPage />}
              />
              <Route
                path="/bend-panel/proizvodi/:id/izmena"
                element={<BandProductFormPage />}
              />
              <Route
                path="/bend-panel/porudzbine"
                element={<BandOrdersPage />}
              />
              <Route
                path="/bend-panel/porudzbine/:orderId"
                element={<BandOrderDetailsPage />}
              />
              <Route
                path="/bend-panel/izvestaji"
                element={<BandReportsPage />}
              />
              <Route
                path="/bend-panel/podesavanja"
                element={<BandSettings />}
              />
            </Route>

            {/* Radio panel - admin i dj rola */}
            <Route element={<ProtectedRoute allowedRoles={["admin", "dj"]} />}>
              <Route path="/admin-panel/radio" element={<AdminRadioPage />} />
              <Route
                path="/admin-panel/radio/nova"
                element={<AdminRadioFormPage />}
              />
            </Route>
        </>

        {/* Javne rute sa parametrima - moraju biti na kraju zbog React Router prioriteta */}
        <Route path="/bend/:bandId" element={<BandDetailsPage />} />
        <Route path="/festival/:festivalId" element={<FestivalDetailsPage />} />

        {/* 404, sve nepoznate rute. Dok se auth učitava (loggedIn === null)
            skeleton umesto crnog ekrana / preranog 404. */}
        <Route
          path="*"
          element={loggedIn === null ? <PageSkeleton full /> : <NotFoundPage />}
        />
      </Routes>
      </Suspense>

      <RadioWidget />

      <Toaster
        position="bottom-right"
        richColors
        theme="dark"
        gap={10}
        toastOptions={{ style: toastBaseStyle }}
      />
      </Fragment>
      </RadioProvider>
    </ThemeProvider>
  );
}

export default App;
